import { AddonPlatform, DataSourceType } from "@coeff/api";
import { AdminExtDataImportRun, DefaultApiGetDataImportRunsRequest } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  Button,
  ChevronLeft,
  ConfirmBox,
  DataSourceInfoWrap,
  Excel,
  FlexColumn,
  FlexRow,
  GoogleSheet,
  ImportTypeIcon,
  LoaderWithPerfTimings,
  Paper,
  PaperContent,
  SortDropdown,
  Tooltip,
  Typography,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { COLORS, KEY_LABEL } from "../../../constants";
import { capitalize, DataSourceTypeDisplayNames, getTimeZone, useApiContext } from "../../../utils";
import { useTableFilters } from "../useTableFilters";

import { DataImportRunDetailsDrawer } from "./DataImportRunDetailsDrawer";
import { DataImportRunsFilterToolbar } from "./DataImportRunsFilterToolbar";
import { getSortIcon } from "./DataImports";
import { IconDropdown } from "./IconDropdown";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const PaginationButton = styled(Button)`
  &&.ant-btn {
    border-radius: 0;
    padding: 14px 8px;
    align-items: center;
    font-size: 12px;

    &:active,
    &:focus,
    &:hover {
      border-color: ${COLORS.coeblue4};
      background-color: transparent;
    }
  }
`;

export const DataImportRuns: React.FC = () => {
  const location = useLocation();

  const [customSQLModalQuery, setCustomSQLModalQuery] = useState<string>();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { adminExtApiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [hoveredDataSourceId, setHoveredDataSourceId] = useState<string>();

  const [activeDataImportRunId, setActiveDataImportRunId] = useState<string>();

  const { data: hoveredDataSourceResponse } = useQuery(
    [QueryCacheKey.ADMIN_EXT_DATA_SOURCE, hoveredDataSourceId],
    () => adminExtApiClient.getDataSource({ data_source_id: hoveredDataSourceId! }),
    { enabled: Boolean(hoveredDataSourceId) }
  );

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "created_dt",
    value: SortDirection.descend,
  });

  const [page, setPage] = React.useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = useState<number>(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetDataImportRunsRequest>([
    "data_import_id",
    "user_id",
    "data_source_type",
    "status",
    "trigger",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const { data: dataImportRunsResponse, isLoading } = useQuery(
    [
      QueryCacheKey.ADMIN_EXT_DATA_IMPORT_RUNS,
      domain,
      pageSize,
      pageOffset,
      sortedColumn,
      search,
      filters,
    ],
    () =>
      adminExtApiClient.getDataImportRuns({
        domain: domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search: search,
        // Filters
        data_import_id: filters.data_import_id?.[0],
        user_id: filters.user_id as unknown as string,
        data_source_type: filters.data_source_type as unknown as string,
        status: filters.status as unknown as string,
        trigger: filters.trigger as unknown as string,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const dataImportRuns = dataImportRunsResponse?.data.data_import_runs ?? [];

  const columns: ColumnsType<AdminExtDataImportRun> = [
    {
      title: "Import Name",
      dataIndex: "data_import_name",
      key: "data_import_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_import_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_import_name"),
      render: (dataImportName: AdminExtDataImportRun["data_import_name"], run) => {
        const sheetName =
          run.import_target_platform === AddonPlatform.GoogleSheets
            ? run.google_sheets_file_name
            : undefined;

        const sheetUrl =
          run.import_target_platform === AddonPlatform.GoogleSheets
            ? run.spreadsheet_url
            : undefined;

        return (
          <FlexRow style={{ maxWidth: 300 }}>
            {run.import_target_platform === AddonPlatform.GoogleSheets ? (
              <GoogleSheet height="24px" width="24px" />
            ) : (
              <Excel height="24px" width="24px" />
            )}

            <FlexColumn gap={0} minWidthZero>
              <Typography title={dataImportName} noWrap fontWeight={500}>
                {dataImportName}
              </Typography>

              <Typography
                title={sheetName}
                noWrap
                color="textSecondary"
                underline={Boolean(sheetUrl)}
                onClick={e => {
                  e.stopPropagation();
                  sheetUrl ? window.open(sheetUrl, "_blank") : null;
                }}
              >
                {sheetName || "-"}
              </Typography>
            </FlexColumn>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.data_source_name}</Typography>,
      dataIndex: "data_source_type",
      key: "data_source_type",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_source_type"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_source_type"),
      render: (dataSourceType: AdminExtDataImportRun["data_source_type"], run) => {
        return (
          <div
            style={{ maxWidth: 200 }}
            onMouseEnter={() => setHoveredDataSourceId(run.source_info?.data_source_id)}
            onMouseLeave={() => setHoveredDataSourceId(undefined)}
          >
            <Tooltip
              variant="light"
              placement="topLeft"
              title={
                hoveredDataSourceId && hoveredDataSourceResponse?.data.data_source ? (
                  <DataSourceInfoWrap dataSource={hoveredDataSourceResponse.data.data_source} />
                ) : undefined
              }
            >
              <FlexRow>
                <ImportTypeIcon size="small" type={run.data_source_type} />

                <Typography noWrap>
                  {run.data_source_name ??
                    DataSourceTypeDisplayNames[dataSourceType as DataSourceType]}
                </Typography>
              </FlexRow>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Ran At</Typography>,
      dataIndex: "created_dt",
      key: "created_dt",
      showSorterTooltip: false,
      width: 160,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_dt" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_dt"),
      render: (_, run) => {
        return (
          <Tooltip
            title={
              run.error_description ? (
                <FlexColumn>
                  <span style={{ fontWeight: "700" }}>Error: </span>
                  {run.error_description}
                </FlexColumn>
              ) : undefined
            }
          >
            <div style={{ display: "inline-block" }}>
              <StatusTag
                status={run.run_status_type === "OK" ? "ok" : "failed"}
                label={`${moment(run.created_dt).format("MMM D, YYYY h:mma")} ${getTimeZone({
                  shortName: true,
                })}`}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.created_by}</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 180,
      render: (createdBy: AdminExtDataImportRun["created_by"], run) => (
        <Typography noWrap title={run.user_email} style={{ maxWidth: 180 }}>
          {createdBy}
        </Typography>
      ),
    },
    {
      title: <Typography noWrap>{KEY_LABEL.duration}</Typography>,
      dataIndex: "run_duration_sec",
      key: "run_duration_sec",
      showSorterTooltip: false,
      width: 120,
      render: (runDurationSec: AdminExtDataImportRun["run_duration_sec"]) => {
        return runDurationSec > 60
          ? `${Math.round(runDurationSec / 60)}m ${Math.round(runDurationSec % 60)}s`
          : `${Math.round(runDurationSec)}s`;
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.trigger_description}</Typography>,
      dataIndex: "trigger_description",
      key: "trigger_description",
      showSorterTooltip: false,
      width: 200,
      render: (triggerDescription: AdminExtDataImportRun["trigger_description"]) =>
        capitalize(triggerDescription),
    },
    {
      title: (
        <Typography noWrap underline title="Includes header row">
          {KEY_LABEL.num_rows}
        </Typography>
      ),
      dataIndex: "num_rows",
      key: "num_rows",
      showSorterTooltip: false,
      width: 100,
      render: (numRows: AdminExtDataImportRun["num_rows"], record) => {
        return record.run_status_type === "OK" ? numRows.toLocaleString() : "-";
      },
    },
    {
      title: <Typography noWrap>{KEY_LABEL.num_cols}</Typography>,
      dataIndex: "num_columns",
      key: "num_columns",
      showSorterTooltip: false,
      width: 100,
      render: (numColumns: AdminExtDataImportRun["num_columns"], record) => {
        return record.run_status_type === "OK" ? numColumns.toLocaleString() : "-";
      },
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (run: AdminExtDataImportRun) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconDropdown
            dropdownProps={{
              overlay: (
                <Menu>
                  {run.source_info?.sql_raw_query ? (
                    <Menu.Item
                      onClick={() =>
                        setCustomSQLModalQuery(
                          run.data_source_details || run.source_info?.sql_raw_query
                        )
                      }
                    >
                      View SQL Query
                    </Menu.Item>
                  ) : null}

                  <Menu.Item
                    onClick={() => {
                      window.open(run.spreadsheet_url, "_blank");
                    }}
                  >
                    Open Sheet
                  </Menu.Item>
                </Menu>
              ),
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Container>
      <DataImportRunsFilterToolbar
        domain={domain}
        search={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          onRow={run => ({
            onClick: () => {
              setActiveDataImportRunId(run.data_import_run_id);
            },
          })}
          columns={columns}
          dataSource={dataImportRuns.map((d, i) => ({ ...d, key: i }))}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="DataImportRuns.locale" />
            ) : (
              "No import runs"
            ),
          }}
          rowKey={r => r.data_import_run_id ?? ""}
          pagination={false}
        />

        <FlexRow style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          <Typography color="textSecondary">Displaying last 30 days</Typography>

          <FlexRow gap={0.5}>
            <PaginationButton
              type="text"
              size="small"
              disabled={page === 0}
              onClick={() => {
                setPageAndOffset(page - 1);
              }}
            >
              <ChevronLeft style={{ height: 11 }} />
            </PaginationButton>

            <PaginationButton
              type="text"
              size="small"
              disabled={!dataImportRunsResponse?.data.has_more}
              onClick={() => {
                setPageAndOffset(page + 1);
              }}
            >
              <ChevronLeft style={{ height: 11, transform: "rotate(180deg)" }} />
            </PaginationButton>
          </FlexRow>
        </FlexRow>
      </FlexColumn>

      <ConfirmBox
        visible={Boolean(customSQLModalQuery)}
        title="Custom SQL Query"
        showCancel={false}
        okButton={<Button type="primary">Done</Button>}
        onCancel={() => setCustomSQLModalQuery(undefined)}
        onOk={() => setCustomSQLModalQuery(undefined)}
        width={480}
        closable
        maskClosable
      >
        <Paper>
          <PaperContent size="small" style={{ position: "relative", paddingBottom: 24 }}>
            <code style={{ fontSize: 12, color: COLORS.black85 }}>{customSQLModalQuery}</code>

            <Button
              size="small"
              onClick={() => {
                navigator.clipboard.writeText(customSQLModalQuery ?? "");
              }}
              style={{ position: "absolute", bottom: 8, right: 8 }}
            >
              Copy
            </Button>
          </PaperContent>
        </Paper>
      </ConfirmBox>

      <DataImportRunDetailsDrawer
        dataImportRunId={activeDataImportRunId}
        onClose={() => setActiveDataImportRunId(undefined)}
      />
    </Container>
  );
};
